import React, { useCallback, useMemo, useState } from 'react';
import { Menu } from '@ui/menus/Menu';
import { Icon, isQuantityIconName } from '@ui/Icon/Icon';
import { IElement } from '@shared/models/project.model';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';
import {
  ElementOriginMapping,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import {
  candidateUnitFrom,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import {
  applyCandidateIfcMeasureOverride,
  listAvailableIfcMeasures,
  resolveActiveIfcMeasure,
  type IfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import { getSelectedElementQuantityProperty } from '@shared/helpers/element-quantity-core.helpers';
import {
  listImportQuantitySources,
  resolveImportQuantity,
  type ImportTarget,
} from '@shared/helpers/import-quantity-resolution.helpers';
import { buildReimportChanges } from '@shared/helpers/csv-import-reimport.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { isElementQuantityExpressionName } from '@shared/helpers/element-quantity.helpers';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useProjectState } from '@client/store/project';
import { useSelectedVersion } from '@client/store/ui';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_UNIT_UPDATE } from '@shared/constants';
import {
  ElementRowEditing,
  useElementRowEditing,
} from '@features/proposals/ElementListRow/use-element-row-editing.hook';
import {
  resolveCandidateProduct,
  resolveCandidateRecipe,
} from './candidate-status.helpers';
import {
  deriveQuantityMappingEntry,
  getSelectableQuantityNames,
  getSelectableQuantityNamesForRecipe,
  importRowDraftPatchForIfcMeasure,
  quantityNameForIfcMeasure,
} from './in-row-mapping.helpers';
import { candidateQuantityFrom } from '@shared/helpers/materialize-candidate.helpers';
import { elementQuantityUnitRecord } from '@shared/models/element-quantities.model';
import { ConversionFactorUnit } from '@shared/models/unit.model';
import { MappingReferenceType } from '@shared/models/element-import.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  buildImportRowDraftChange,
  buildImportRowDraftReplaceChange,
  getImportRowDraft,
} from '@shared/helpers/import-row-draft.helpers';
import { useElementImports } from './use-element-imports.hook';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { BulkRowActions } from './use-bulk-row-actions.hook';

type UseImportedRowEditingArgs = {
  row: CandidateRow;
  element: IElement;
  /** Fans quantity and unit edits out to the multi-selection (DEV-3456). */
  bulk: BulkRowActions;
};

export type ImportedRowEditing = ElementRowEditing & {
  /** IFC measures present on the file row (for the metadata-strip picker). */
  fileMeasures: readonly IfcMeasure[];
  /** Resolved active measure (draft override or parse-time source). */
  activeIfcMeasure: IfcMeasure | undefined;
  /** Draft override only — used to overlay strip quantity/unit values. */
  activeIfcMeasureDraft: IfcMeasure | undefined;
  onPickIfcMeasure: (measure: IfcMeasure) => void;
  product: IProduct | undefined;
  recipe: Recipe | undefined;
  isRecipeMapping: boolean;
};

/**
 * In-row editing surface for a materialized CSV row. Reuses
 * {@link useElementRowEditing} for display + name, but quantity and unit edits
 * write to the element's origin *mapping* (deriving a new conversion factor)
 * and re-create the element in place — preserving its id and every user edit.
 *
 * Unit options are the element's quantity rows the mapped product can actually
 * convert to (so a product without an `m` factor never offers "length"). Picking
 * one steers which quantity the Mängd is written to; the LCA pipeline converts
 * to kg downstream.
 */
export const useImportedRowEditing = ({
  row,
  element,
  bulk,
}: UseImportedRowEditingArgs): ImportedRowEditing => {
  const base = useElementRowEditing(element);

  const { project } = useProjectState('project');
  const imports = useElementImports(project?.id ?? -1);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const version = useSelectedVersion();
  const applyChanges = useApplyProjectChanges();

  const [unitMenuAnchor, setUnitMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const mappingsByCode =
    imports.status === 'ready' ? imports.data.mappingsByCode : undefined;
  const importsById =
    imports.status === 'ready' ? imports.data.importsById : undefined;

  const origin =
    element.origin?.kind === 'csvImport' ? element.origin : undefined;
  const overlayMapping =
    origin?.source === 'inline' ? origin.mapping : undefined;
  const dbMapping = row.resourceCode
    ? mappingsByCode?.[row.resourceCode]
    : undefined;
  const effectiveMapping = overlayMapping ?? dbMapping;
  const referenceId =
    effectiveMapping?.referenceType === 'product'
      ? effectiveMapping.referenceId
      : undefined;
  const recipeReferenceId =
    effectiveMapping?.referenceType === 'recipe'
      ? effectiveMapping.referenceId
      : undefined;
  const product =
    resolveCandidateProduct(
      row,
      mappingsByCode ?? {},
      productsById,
      overlayMapping,
    ) ?? (referenceId ? productsById[referenceId] : undefined);
  const recipe =
    resolveCandidateRecipe(
      row,
      mappingsByCode ?? {},
      recipesById,
      overlayMapping,
    ) ?? (recipeReferenceId ? recipesById[recipeReferenceId] : undefined);
  // The persisted referenceType is the discriminant — a recipe-mapped row stays
  // on the recipe path even before recipesById has loaded the recipe object. An
  // absent type means a legacy product-only mapping.
  const referenceType: MappingReferenceType =
    effectiveMapping?.referenceType === 'recipe' ? 'recipe' : 'product';
  const isRecipeMapping = referenceType === 'recipe';

  const draft = version ? getImportRowDraft(version, row.id) : {};
  const fileMeasures = useMemo(() => listAvailableIfcMeasures(row), [row]);
  // The unit button reads the proposal-resolved element. Mark the menu from
  // that same element, so a pieces pick that only exists once the proposal
  // is applied stays selected after reload.
  const currentName = getSelectedElementQuantityProperty(
    base.displayElement,
  )?.name;

  // The column the row was actually imported through, which is not always the
  // one the parser picked: a target that cannot convert metres imports the same
  // row off its volume instead. The strip and the measure picker show this, so
  // what the row says matches what its element was built from.
  const activeIfcMeasure = useMemo(() => {
    const pinned = resolveActiveIfcMeasure(row, draft.ifcMeasure);
    const target: ImportTarget | undefined = isRecipeMapping
      ? recipe && { kind: 'recipe', recipe }
      : product && { kind: 'product', product };
    if (!target) return pinned;

    const [primary] = listImportQuantitySources(row, pinned);
    const resolution = resolveImportQuantity({
      row,
      target,
      pinnedMeasure: pinned,
      explicitQuantity: isElementQuantityExpressionName(currentName)
        ? currentName
        : undefined,
      manualFactor: primary
        ? effectiveMapping?.mappings[primary.rowUnit]?.quantityFactor
        : undefined,
    });
    if (resolution.status === 'unresolvable') return pinned;
    return resolution.measure ?? pinned;
  }, [
    row,
    draft.ifcMeasure,
    isRecipeMapping,
    recipe,
    product,
    currentName,
    effectiveMapping,
  ]);

  // Element quantities the user can map to: a product's convertible ones, or a
  // recipe category's defined ones (DEV-3387). The recipe object is only metadata
  // here; the row stays recipe-backed regardless of whether it has loaded.
  const unitOptions = useMemo(
    () =>
      isRecipeMapping
        ? getSelectableQuantityNamesForRecipe(recipe)
        : getSelectableQuantityNames(product),
    [isRecipeMapping, recipe, product],
  );

  const rederive = useCallback(
    (
      name: ElementQuantityExpressionName,
      typedTotal: number | undefined,
      measureOverride: IfcMeasure | undefined = activeIfcMeasure,
    ) => {
      const sourceRow = applyCandidateIfcMeasureOverride(row, measureOverride);
      const sourceUnit = candidateUnitFrom(sourceRow);
      if (!sourceUnit || !version || !importsById) return;

      const existingMappings = effectiveMapping?.mappings ?? {};
      let overlay: ElementOriginMapping;
      switch (referenceType) {
        // Recipe: write the CSV Mängd straight into the picked element quantity
        // (factor maps directly; recipes have no conversion factors).
        case 'recipe': {
          const recipeId = recipeReferenceId ?? recipe?.id;
          if (!recipeId) return;
          const qtyFrom = candidateQuantityFrom(sourceRow);
          const factor =
            typedTotal !== undefined && qtyFrom ? typedTotal / qtyFrom : 1;
          overlay = {
            referenceId: recipeId,
            referenceType: 'recipe',
            mappings: {
              ...existingMappings,
              [sourceUnit]: {
                quantityFactor: factor,
                quantityName: name,
                unitTo: elementQuantityUnitRecord[name] as
                  ConversionFactorUnit | undefined,
              },
            },
          };
          break;
        }
        case 'product': {
          if (!product) return;
          const entry = deriveQuantityMappingEntry({
            row: sourceRow,
            product,
            quantityName: name,
            typedTotal,
            // Re-target an existing CSV->nodon factor into the picked quantity's
            // unit via the product's factors (e.g. st->kg becomes st->m²).
            fromEntry: existingMappings[sourceUnit],
          });
          if (!entry) return;
          overlay = {
            referenceId: referenceId ?? product.id,
            referenceType: 'product',
            mappings: { ...existingMappings, [sourceUnit]: entry },
          };
          break;
        }
        default:
          return assertUnreachable(referenceType);
      }

      const result = tryMaterializeCandidateRow(row, {
        mappingsByCode: mappingsByCode ?? {},
        productsById,
        recipesById,
        importsById,
        draftMappingsByRowId: { [row.id]: overlay },
        // Carry the typed total as a `count.value` override (on top of the
        // conversion expression the product path also bakes into the factor), so
        // the number override is durable and survives a later product switch
        // (DEV-3439).
        quantityOverridesByRowId:
          typedTotal !== undefined ? { [row.id]: typedTotal } : undefined,
        selectedQuantityByRowId: { [row.id]: name },
        ifcMeasureByRowId: measureOverride
          ? { [row.id]: measureOverride }
          : undefined,
        trigger: 'user',
      });
      if (!result.ok) return;
      const changes = buildReimportChanges({
        existing: element,
        fresh: result.element,
      });
      if (measureOverride) {
        const draftChange = buildImportRowDraftChange(version, row.id, {
          ifcMeasure: measureOverride,
          selectedQuantity: name,
          unitTo: elementQuantityUnitRecord[name] as
            ConversionFactorUnit | undefined,
          quantityFactor: undefined,
          expression: undefined,
        });
        if (draftChange) changes.push(draftChange);
      }
      if (changes.length) void applyChanges(changes);
    },
    [
      activeIfcMeasure,
      applyChanges,
      effectiveMapping,
      element,
      importsById,
      referenceType,
      mappingsByCode,
      product,
      productsById,
      recipe,
      recipesById,
      recipeReferenceId,
      referenceId,
      row,
      version,
    ],
  );

  // Quantity edit: the typed total stays in the currently selected quantity.
  const handleQtySave = useCallback(
    async (value: number | undefined) => {
      if (value !== undefined && bulk.shouldFanOut(row.id)) {
        bulk.setTotalQuantity(value, row.id);
        return;
      }
      if (!currentName || !isElementQuantityExpressionName(currentName)) {
        return;
      }
      // Erasing the override clears only the count's `value` and leaves the
      // conversion expression untouched, so its result becomes the fallback —
      // mirrors the Elements-tab / side-panel behavior (DEV-3487).
      if (value === undefined) {
        // Clear the proposal-resolved count, the same element currentName comes from.
        const count = getSelectedElementQuantityProperty(
          base.displayElement,
        )?.count;
        if (count?.value === undefined) return;
        void applyChanges([
          {
            type: ProjectChangeType.ElementQuantityCount,
            elementId: element.id,
            quantityName: currentName,
            count: { ...count, value: undefined },
          },
        ]);
        return;
      }
      rederive(currentName, value);
    },
    [
      applyChanges,
      base.displayElement,
      bulk,
      currentName,
      element.id,
      rederive,
      row.id,
    ],
  );

  // Unit pick: write the CSV Mängd to the picked quantity row (auto-converted).
  const handlePickUnit = useCallback(
    (name: ElementQuantityExpressionName) => {
      setUnitMenuAnchor(null);
      if (bulk.shouldFanOut(row.id)) bulk.setQuantityName(name);
      else rederive(name, undefined);
      amplitudeLog(AMP_ELEMENT_UNIT_UPDATE, {
        ElementID: element.id,
        unit: name,
      });
    },
    [bulk, element.id, rederive, row.id],
  );

  const handlePickIfcMeasure = useCallback(
    (measure: IfcMeasure) => {
      if (bulk.shouldFanOut(row.id)) {
        bulk.setIfcMeasure(measure);
        return;
      }
      const nextQuantity = quantityNameForIfcMeasure(
        row,
        measure,
        isRecipeMapping ? undefined : product,
        isRecipeMapping ? recipe : undefined,
      );
      if (!nextQuantity) {
        if (!version) return;
        const nextDraft: ImportRowDraft = {
          referenceId:
            referenceId ?? product?.id ?? recipeReferenceId ?? recipe?.id,
          referenceType,
          ...importRowDraftPatchForIfcMeasure(
            row,
            measure,
            isRecipeMapping ? undefined : product,
            isRecipeMapping ? recipe : undefined,
          ),
        };
        const changes: ProjectChange[] = [
          {
            type: ProjectChangeType.ElementRemove,
            elementId: element.id,
          },
        ];
        const draftChange = buildImportRowDraftReplaceChange(
          version,
          row.id,
          nextDraft.referenceId ? nextDraft : { ifcMeasure: measure },
        );
        if (draftChange) changes.push(draftChange);
        void applyChanges(changes);
        return;
      }
      rederive(nextQuantity, undefined, measure);
    },
    [
      applyChanges,
      bulk,
      element.id,
      isRecipeMapping,
      product,
      recipe,
      recipeReferenceId,
      rederive,
      referenceId,
      referenceType,
      row,
      version,
    ],
  );

  const handleUnitClick = useCallback<React.MouseEventHandler<HTMLElement>>(
    (e) => {
      e.stopPropagation();
      setUnitMenuAnchor(e.currentTarget);
    },
    [],
  );

  // The selector picks which element quantity gets filled — gated only by what
  // the product can represent (>1 option). The CSV unit's auto-convertibility is
  // irrelevant: the factor that fills the picked quantity is derived separately.
  const canPickUnit = unitOptions.length > 1;
  const unitMenuOpen = unitMenuAnchor !== null;
  const unitMenu = (
    <Menu
      open={unitMenuOpen}
      anchorEl={unitMenuAnchor}
      onClose={() => setUnitMenuAnchor(null)}
    >
      {unitMenuOpen &&
        unitOptions.map((name) => (
          <Menu.Item
            key={name}
            selected={name === currentName}
            icon={
              isQuantityIconName(name) ? (
                <Icon category="quantity" icon={name} />
              ) : null
            }
            onClick={() => handlePickUnit(name)}
          >
            {labelFromValue(name, true)}
          </Menu.Item>
        ))}
    </Menu>
  );

  return {
    ...base,
    handleQtySave,
    // Quantity is mapping-driven, so the direct expression panel is disabled.
    onQtyFxClick: undefined,
    onUnitClick: canPickUnit ? handleUnitClick : undefined,
    unitMenu,
    fileMeasures,
    activeIfcMeasure,
    activeIfcMeasureDraft: draft.ifcMeasure,
    onPickIfcMeasure: handlePickIfcMeasure,
    product,
    recipe,
    isRecipeMapping,
  };
};
